"use client";

import { useState, type ReactNode } from "react";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import {
  ALL_DECISIONS,
  STATE_BY_OPTIONS,
  STATE_SCOPE_OPTIONS,
  STATE_TIME_UNIT_OPTIONS,
  nextStateFilter,
  type HideReviewedValue,
  type StateBy,
  type StateFilterValue,
  type StateScope,
  type StateTimeUnit,
} from "@/features/search/lib/state-filters";
import {
  REVIEW_DECISION_LABELS,
  type ReviewDecision,
} from "@/features/search/lib/reviews";
import { FilterCheckboxInput } from "@/features/search/components/filters/input/checkbox-input";
import { FilterNumberInput } from "@/features/search/components/filters/input/number-input";
import { FilterSegmentInput } from "@/features/search/components/filters/input/segment-input";
import { FilterSelectInput } from "@/features/search/components/filters/input/select-input";
import {
  FilterFieldContainer,
  FilterFieldContent,
  FilterFieldLabel,
  FilterLabel,
} from "../shared";

/**
 * Custom time value: keeps a local draft so the field can be cleared and
 * retyped; only valid 1..999 numbers are committed, and an empty field is
 * restored to the committed value on blur.
 */
function TimeValueInput({
  value,
  onCommit,
  label,
}: {
  value: number;
  onCommit: (v: number) => void;
  label: string;
}) {
  const [draft, setDraft] = useState(String(value));
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(String(value));
  }
  return (
    <div
      onBlurCapture={() => {
        if (draft === "") setDraft(String(value));
      }}
    >
      <FilterNumberInput
        aria-label={label}
        value={draft === "" ? undefined : Number(draft)}
        onChange={(v) => {
          if (v === undefined) {
            setDraft("");
            return;
          }
          setDraft(String(v));
          onCommit(v);
        }}
        min={1}
        max={999}
        placeholder="6"
      />
    </div>
  );
}

function StateFilterControl<T extends StateFilterValue>({
  label,
  name,
  value,
  onChange,
  defaults,
  extra,
}: {
  label: string;
  /** Lowercase noun for accessible names, e.g. "Hide viewed". */
  name: string;
  value: T | null | undefined;
  onChange: (next: T | undefined) => void;
  defaults?: Partial<T>;
  extra?: ReactNode;
}) {
  const scope = value?.scope ?? "none";
  const by = value?.by ?? "anyone";
  const timeRange = value?.time_range ?? "all_time";
  const timeValue = value?.time_value ?? 6;
  const timeUnit = value?.time_unit ?? "months";

  const update = (patch: Partial<T>) =>
    onChange(nextStateFilter<T>(value, patch, defaults));

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <FilterLabel>{label}</FilterLabel>

      <FilterSelectInput<StateScope>
        options={STATE_SCOPE_OPTIONS}
        value={scope}
        onChange={(v) => update({ scope: v } as Partial<T>)}
        placeholder="Don't hide"
        aria-label={`${name} scope`}
      />

      {scope !== "none" && (
        <div className="flex flex-col gap-1">
          <FilterSelectInput<StateBy>
            options={STATE_BY_OPTIONS}
            value={by}
            onChange={(v) => update({ by: v } as Partial<T>)}
            placeholder="By anyone"
            aria-label={`${name} by`}
          />
          {extra}
          <FilterSegmentInput
            aria-label={`${name} time range`}
            options={["All Time", "Custom"]}
            value={timeRange === "custom" ? "Custom" : "All Time"}
            onChange={(v) =>
              update({
                time_range: v === "Custom" ? "custom" : "all_time",
              } as Partial<T>)
            }
          />
          {timeRange === "custom" && (
            <div className="flex gap-1.5">
              <div className="w-20">
                <TimeValueInput
                  value={timeValue}
                  onCommit={(v) => update({ time_value: v } as Partial<T>)}
                  label={`${name} time value`}
                />
              </div>
              <FilterSelectInput<StateTimeUnit>
                options={STATE_TIME_UNIT_OPTIONS}
                value={timeUnit}
                onChange={(v) => update({ time_unit: v } as Partial<T>)}
                placeholder="Months"
                aria-label={`${name} time unit`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function HideViewedControl() {
  const { filters, updateFilter } = useTalentSearchFilters();
  return (
    <StateFilterControl<StateFilterValue>
      label="Hide Viewed Profiles"
      name="Hide viewed"
      value={filters.hide_viewed}
      onChange={(next) => updateFilter("hide_viewed", next)}
    />
  );
}

export function HideReviewedControl() {
  const { filters, updateFilter } = useTalentSearchFilters();
  const value = filters.hide_reviewed as HideReviewedValue | null | undefined;
  const decisions = value?.decisions?.length ? value.decisions : ALL_DECISIONS;

  const toggle = (decision: ReviewDecision, on: boolean) => {
    const set = new Set(decisions);
    if (on) set.add(decision);
    else set.delete(decision);
    // At least one decision must stay selected.
    if (set.size === 0) return;
    updateFilter(
      "hide_reviewed",
      nextStateFilter<HideReviewedValue>(value, {
        decisions: ALL_DECISIONS.filter((d) => set.has(d)),
      }),
    );
  };

  return (
    <StateFilterControl<HideReviewedValue>
      label="Hide Reviewed Profiles"
      name="Hide reviewed"
      value={value}
      defaults={{ decisions: ALL_DECISIONS }}
      onChange={(next) => updateFilter("hide_reviewed", next)}
      extra={
        <div
          role="group"
          aria-label="Hide reviewed decisions"
          className="flex flex-col gap-1 py-1"
        >
          <span className="text-xs font-medium text-contrast/60">
            Hide candidates marked
          </span>
          {ALL_DECISIONS.map((d) => (
            <FilterCheckboxInput
              key={d}
              label={REVIEW_DECISION_LABELS[d]}
              checked={decisions.includes(d)}
              onChange={(on) => toggle(d, on)}
            >
              {REVIEW_DECISION_LABELS[d]}
            </FilterCheckboxInput>
          ))}
        </div>
      }
    />
  );
}

export function FilterGeneralSection() {
  const { filters, updateFilter } = useTalentSearchFilters();

  return (
    <>
      <FilterFieldContainer>
        <FilterFieldLabel>Minimum Experience (Years)</FilterFieldLabel>
        <FilterFieldContent>
          <FilterNumberInput
            value={filters.min_experience_years ?? undefined}
            onChange={(v) => updateFilter("min_experience_years", v)}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      <FilterFieldContainer>
        <FilterFieldLabel>Maximum Experience (Years)</FilterFieldLabel>
        <FilterFieldContent>
          <FilterNumberInput
            value={filters.max_experience_years ?? undefined}
            onChange={(v) => updateFilter("max_experience_years", v)}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      <HideViewedControl />
      <HideReviewedControl />
      <FilterCheckboxInput
        label="Remove your People Exclusions from results"
        checked={filters.exclude_org_profiles ?? true}
        onChange={(v) => updateFilter("exclude_org_profiles", v)}
      >
        Remove your{" "}
        <span className="text-primary dark:text-[#91acff] underline decoration-solid">People Exclusions</span> from
        results
      </FilterCheckboxInput>
    </>
  );
}
